import { DateTime } from 'luxon';
import type { TripLegAttestationItem } from '../data/models/trip-leg.model';
import { TripLegStatusBC } from '../data/models/trip-leg-status.model';

/**
 * Parameters for handling timestamp updates
 */
export interface TimestampUpdateParams {
  newTime: string;
  field: string;
  tripLeg: TripLegAttestationItem;
  currentValue: DateTime | undefined;
  canceledLateTime?: DateTime;
  noLoadTime?: DateTime;
}

/**
 * Result of timestamp update validation and processing
 */
export interface TimestampUpdateResult {
  isValid: boolean;
  isoTimestamp?: string;
  fieldName?: string;
  timestampExists?: boolean;
}

/**
 * Validates and processes a timestamp update from user input.
 *
 * @param params - The timestamp update parameters
 * @returns Result object indicating if update is valid and containing processed data
 */
export function processTimestampUpdate(
  params: TimestampUpdateParams,
): TimestampUpdateResult {
  const {
    newTime,
    field,
    tripLeg,
    currentValue,
    canceledLateTime,
    noLoadTime,
  } = params;

  // Check if the input is a placeholder
  const isPlaceholder = newTime === 'hh:mm aa';
  if (isPlaceholder) {
    return { isValid: false };
  }

  // Get the appointment date
  const date = tripLeg.appointmentDate;

  // Determine the correct field name based on status
  let fieldName = field.toUpperCase();
  if (field === 'onSite' && canceledLateTime) {
    fieldName = TripLegStatusBC.CanceledLate.toString().toUpperCase();
  }
  if (
    field === 'onBoard' &&
    (noLoadTime || tripLeg.tripLegStatusBC === TripLegStatusBC.NoLoad)
  ) {
    fieldName = TripLegStatusBC.NoLoad.toString().toUpperCase();
  }

  // Convert the new time to UTC
  const newDateTime = DateTime.fromFormat(
    `${date} ${newTime}`,
    'yyyy-MM-dd h:mm a',
    { zone: tripLeg.pickUpAddressTimeZoneIANA },
  ).toUTC();

  // Check if the new time is the same as the current time
  const isSame =
    newDateTime.startOf('minute').toISO() ===
    currentValue?.startOf('minute').toISO();

  // Validate the new date time and check if it's different
  if (!newDateTime.isValid || isSame) {
    return { isValid: false };
  }

  // Convert to ISO timestamp
  const isoTimestamp = newDateTime.setZone('utc').toISO()!;

  // Check if timestamp already exists in the trip leg logs
  const timestampExists = Boolean(
    tripLeg.tripLegStatusLogs?.find((log) => log.tripLegStatusBC === fieldName),
  );

  return {
    isValid: true,
    isoTimestamp,
    fieldName,
    timestampExists,
  };
}
